import { Button, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconEye,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import routes from '@/routes';
import userMutations from '@/api/mutitations/user';
import docsMutation from '@/api/mutitations/docs';
import PageTemplate from '@/components/PageTemplate';
import Table from '@/components/Table';
import { useState } from 'react';
import { extractFilterOptions, initialColumnFilters } from '@/components/Table/Table.functions';
import Userview from '@/components/UserView';
import { useDisclosure } from '@mantine/hooks';
import UserEdit from '@/components/UserEdit';
import UserCreate from '@/components/userCreate';
import Modal from '@/components/Modal/Index';
import DetailsModal from '@/components/DetailsModal';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation';

const Users = () => {
  const t = prepareTranslate();
  const [filterOptions, setFilterOptions] = useState({});
  const { useGetUsers, useDeleteManyUsers } = userMutations;
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();

  const { mutateAsync: getUsersMutate } = useGetUsers();
  const { mutateAsync: deleteMutate } = useDeleteManyUsers();

  const [createOpened, { open: createOpen, close: createClose }] = useDisclosure();
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure();
  const [editOpened, { open: editOpen, close: editClose }] = useDisclosure();

  const [selectedUser, setSelectedUser] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);

  // eslint-disable-next-line consistent-return
  const getData = async (start, end, query, sort) => {
    try {
      const result = await getUsersMutate({
        start,
        end,
        query,
        sort,
      });

      const newFilterOptions = extractFilterOptions(result.data, mockColumns);
      setFilterOptions(newFilterOptions);
      return result;
    } catch (error) {
      notifications.show({
        title: t('pages.users.notification.errors.find.title'),
        message: t('pages.users.notification.errors.find.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };
  const handleClickOnView = (id) => {
    setSelectedUser(id);
    viewOpen();
  };

  const handleClickOnEdit = (id) => {
    setSelectedUser(id);
    editOpen();
  };

  const handleCreateClick = () => {
    createOpen();
  };

  // eslint-disable-next-line consistent-return
  const createFile = async (option, query) => {
    const requestColumns = mockColumns.filter((column) => column.title !== 'פעולות');
    const exportData = {
      query,
      fileType: option,
      service: 'user',
      columns: requestColumns,
      titleArray: ['firstName'],
    };

    const fileTitleBase = t('pages.users.fileTitle');
    const fileTitle = option === 'pdf' ? `${fileTitleBase}.pdf` : `${fileTitleBase}.xlsx`;

    try {
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.users.notification.errors.export.title'),
        message: t('pages.users.notification.errors.export.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };
  const form = useForm({
    validate: {
      deletionReason: (value) => validations.required(value),
    },
  });

  const actionResultClose = () => setActionResultOpened(false);
  const actionResultOpen = () => setActionResultOpened(true);
  const [actionResultOpened, setActionResultOpened] = useState(false);
  const [resultLabel, setResultLabel] = useState('');
  const [resultActions, setResultActions] = useState([]);
  const handleModalClose = () => {
    actionResultClose();
  };
  const sucessResultActions = [
      <Button key='closeButton' onClick={handleModalClose}>{t('global.close')}</Button>,
  ];

  const faildResultActions = [
      <Button key='closeButton' onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const handleClickOnDelete = async () => {
    form.validate();

    try {
      await deleteMutate({
        data: selectedForDelete,
        deletionReason: form.values.deletionReason,
      });
      setResultLabel(t('pages.users.succesdDelete'));
      setResultActions(sucessResultActions);
      actionResultOpen();
      setSelectedForDelete(null);
      deleteClose();
      form.reset();
      setRefreshKey((prev) => prev + 1);
    } catch {
      setResultLabel(t('pages.users.faildDelete'));
      setResultActions(faildResultActions);
      actionResultOpen();
    }
  };

  const mockColumns = [
    {
      title: 'שם פרטי',
      accessor: 'firstName',
      isVisible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: 'שם משפחה',
      accessor: 'lastName',
      isVisible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: 'דואר אלקטרוני',
      accessor: 'email',
      transforms: ['email'],
      isVisible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: 'טלפון',
      accessor: 'phone',
      isVisible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: 'חסום',
      accessor: 'isBlocked',
      transforms: ['yesno'],
      isVisible: true,
      sortable: true,
    },
    {
      title: 'התחבר לאחרונה',
      accessor: 'lastLoginAttempt',
      transforms: ['date'],
      isVisible: true,
      sortable: true,
    },
    {
      title: 'ארכיון',
      accessor: 'isArchived',
      isVisible: true,
      sortable: true,
      transforms: ['yesno'],
    },
    {
      title: 'תפקיד',
      accessor: 'role',
      isVisible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          action: handleClickOnView,
          icon: <IconEye />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: 'עריכה',
          action: handleClickOnEdit,
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
        },
        // TODO: block/unblock user.
        // {
        //   title: 'הסרת חסימה',
        //   action: handleUnBlockClick,
        //   icon: <IconX />,
        //   showLabel: false,
        //   isPrimary: false,
        // },
      ],
    },
  ];

  const mockSelectionActions = [
    {
      title: 'מחיקה',
      icon: <IconX stroke='1' />,
      onClick: (data) => {
        setSelectedForDelete(data);
        deleteOpen();
      },
    },
  ];

  const exportOptions = ['excel'];

  const initialColumnFilters2 = initialColumnFilters(mockColumns);
  const [columnFilters, setColumnFilters] = useState(initialColumnFilters2);

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={handleClickOnDelete}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={() => deleteClose()} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <>
    <PageTemplate
      title={routes.users.name}
      actions={[
        <Button key='createUser' leftSection={<IconPlus />} onClick={handleCreateClick}>
          {t('pages.users.action')}
        </Button>]
      }
    >

        <Table
          columns={mockColumns}
          getData={getData}
          selectionActions={mockSelectionActions}
          isColumnsActive={false}
          exportOptions={exportOptions}
          isExportActive
          createFile={createFile}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          filterOptions={filterOptions}
          showSelectionColumn = {true}
          key={refreshKey}
        />
      </PageTemplate>

      <UserCreate
        opened = {createOpened}
        close={createClose}
        setKey={setRefreshKey}
      />

      <Userview
        opened={viewOpened}
        close={viewClose}
        id={selectedUser}
        editUser ={editOpen}
      />

      <UserEdit
        opened={editOpened}
        close={editClose}
        id={selectedUser}
        setKey={setRefreshKey}
      />
      <Modal
        opened={actionResultOpened}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
      <DetailsModal
        opened={deleteOpened}
        onClose={deleteClose}
        label={t('pages.users.verifyDeletion')}
        actions={deleteActions}
      >
        <TextInput
          required
          {...form.getInputProps('deletionReason')}
          label={t('pages.users.deletionReason')}
        />
      </DetailsModal>
    </>
  );
};

export default Users;
